import PropTypes from 'prop-types';
import clsx from 'clsx';

/**
 * Small form primitives shared by the simulation builder's editors.
 *
 * Numbers are kept exactly as typed while editing (so clearing a box does not snap it
 * to 0 under the trainer's cursor) and converted once, at save time, by the builder.
 */

export const inputCls =
  'w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-800 placeholder:text-neutral-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-neutral-50 disabled:text-neutral-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100 dark:focus:ring-primary-500/20 dark:disabled:bg-neutral-800/60';

/** A labelled field. `hint` sits under the control. */
export const Field = ({ label, hint, className, children }) => (
  <label className={clsx('block', className)}>
    <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <span className="mt-1 block text-[11px] text-neutral-400 dark:text-neutral-500">{hint}</span>}
  </label>
);

Field.propTypes = {
  label: PropTypes.node.isRequired,
  hint: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export const TextInput = ({ value, onChange, ...rest }) => (
  <input value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={inputCls} {...rest} />
);

TextInput.propTypes = { value: PropTypes.string, onChange: PropTypes.func.isRequired };

export const TextArea = ({ value, onChange, rows = 3, ...rest }) => (
  <textarea
    rows={rows}
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    className={clsx(inputCls, 'resize-y')}
    {...rest}
  />
);

TextArea.propTypes = { value: PropTypes.string, onChange: PropTypes.func.isRequired, rows: PropTypes.number };

/** A number box that keeps '' while empty; the builder converts on save. */
export const NumberInput = ({ value, onChange, ...rest }) => (
  <input
    type="number"
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
    className={clsx(inputCls, 'tabular-nums')}
    {...rest}
  />
);

NumberInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
};

/**
 * A list of short strings edited as one line each. Blank lines are kept while typing
 * (so Enter makes a new line) and dropped at save time.
 */
export const LinesInput = ({ value, onChange, rows = 3, ...rest }) => (
  <textarea
    rows={rows}
    value={(value || []).join('\n')}
    onChange={(e) => onChange(e.target.value.split('\n'))}
    className={clsx(inputCls, 'resize-y')}
    {...rest}
  />
);

LinesInput.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  rows: PropTypes.number,
};

/** The section header used at the top of each editor card. */
export const EditorHeading = ({ title, children }) => (
  <div className="mb-3">
    <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{title}</h3>
    {children && <p className="mt-1 text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">{children}</p>}
  </div>
);

EditorHeading.propTypes = { title: PropTypes.node.isRequired, children: PropTypes.node };
